import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Loader2, Search, ShieldCheck, AlertTriangle, AlertCircle, X,
  Download, ClipboardCheck, FileText, Building2, Eye, Mail, Bell, Paperclip, Pencil, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import EditCredentialModal from '../components/EditCredentialModal';
import ComposeModal from '../components/ComposeModal';
import { buildCredentialReminder } from '../utils/credentialReminder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  current:  'bg-emerald-100 text-emerald-700 border-emerald-200',
  expiring: 'bg-amber-100 text-amber-800 border-amber-200',
  expired:  'bg-red-100 text-red-700 border-red-200',
  missing:  'bg-gray-100 text-gray-500 border-gray-200',
};
const STATUS_DOT = {
  current:  'bg-emerald-500',
  expiring: 'bg-amber-500',
  expired:  'bg-red-500',
  missing:  'bg-gray-300',
};

const credKey = (row, c) => c.document_id || `missing:${row.personnel_id}:${c.type}`;

export default function CredentialAudit() {
  const [personnel, setPersonnel] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedTypes, setSelectedTypes] = useState(new Set()); // empty = ALL active credential types
  const [search, setSearch] = useState('');
  const [warningDays, setWarningDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all | missing | expired | expiring | current
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  // Selection keys: real document id, or "missing:<pid>:<type>" for missing creds (reminder-only).
  const realDocIds = useMemo(() => Array.from(selectedDocIds).filter(k => !String(k).startsWith('missing:')), [selectedDocIds]);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(null); // {document_id, document_name, file_name}
  const [editingDocId, setEditingDocId] = useState(null);
  const [reminderPrefill, setReminderPrefill] = useState(null); // {to, subject, body} or null
  // Bulk reminder setup: choose sender mailbox + resolve people with no email.
  const [bulkSetup, setBulkSetup] = useState(null); // { people: [...] } or null
  const [bulkMailbox, setBulkMailbox] = useState('shared'); // 'shared' (admin) | 'personal' (me)
  const [senderConns, setSenderConns] = useState({ shared: null, personal: null });
  const [emailAttachPrefill, setEmailAttachPrefill] = useState(null); // {document_ids, label} or null

  // Agency filter
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyIds, setSelectedAgencyIds] = useState(new Set());
  const [agencySearch, setAgencySearch] = useState('');
  const [agencySinceDays, setAgencySinceDays] = useState(0);  // 0 = all-time
  const [loadingAgencyFilter, setLoadingAgencyFilter] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/personnel?status=Active`),
      axios.get(`${API}/credentials/types`),
      axios.get(`${API}/agency-profiles`),
    ]).then(([p, t, a]) => {
      setPersonnel(p.data || []);
      setAllTypes(t.data?.types || []);
      setAgencies((a.data || []).slice().sort((x, y) => (x.agency_name || '').localeCompare(y.agency_name || '')));
    });
  }, []);

  const filteredAgencies = useMemo(() => {
    if (!agencySearch.trim()) return agencies;
    const s = agencySearch.toLowerCase();
    return agencies.filter(a => (a.agency_name || '').toLowerCase().includes(s));
  }, [agencies, agencySearch]);

  const toggleAgency = (id) => {
    setSelectedAgencyIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyAgencyFilter = async () => {
    if (selectedAgencyIds.size === 0) {
      toast.error('Pick at least one agency first');
      return;
    }
    setLoadingAgencyFilter(true);
    try {
      const params = { agency_ids: Array.from(selectedAgencyIds).join(',') };
      if (agencySinceDays && agencySinceDays > 0) params.since_days = agencySinceDays;
      const res = await axios.get(`${API}/admin/credentials/personnel-by-agency`, { params });
      const found = res.data?.personnel || [];
      if (found.length === 0) {
        toast.warning('No therapists found for selected agency/agencies in that timeframe');
        return;
      }
      // Replace the selected therapists with the agency-derived list
      setSelectedIds(new Set(found.map(x => x.personnel_id)));
      toast.success(`Selected ${found.length} therapist(s) from ${selectedAgencyIds.size} agency/agencies`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load therapists for agency');
    } finally {
      setLoadingAgencyFilter(false);
    }
  };

  const filteredPeople = useMemo(() => {
    if (!search.trim()) return personnel;
    const s = search.toLowerCase();
    return personnel.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(s));
  }, [personnel, search]);

  const toggleId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runReport = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one therapist');
      return;
    }
    setLoading(true);
    try {
      const body = {
        personnel_ids: Array.from(selectedIds),
        warning_days: parseInt(warningDays) || 30,
      };
      if (selectedTypes.size > 0) body.credential_types = Array.from(selectedTypes);
      const res = await axios.post(`${API}/admin/credentials/coverage-report`, body);
      setReport(res.data);
      setSelectedDocIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report failed');
    } finally { setLoading(false); }
  };

  const downloadCsv = () => {
    if (!report) return;
    const head = ['Name', 'Discipline', ...report.credential_types].join(',');
    const lines = [head];
    report.rows.forEach(r => {
      const cells = [
        `"${r.name}"`,
        `"${r.discipline || ''}"`,
        ...report.credential_types.map(t => {
          const c = r.credentials.find(x => x.type === t);
          if (!c) return '';
          if (c.status === 'missing') return 'MISSING';
          if (c.status === 'expired') return `EXPIRED ${c.expiration_date || ''}`;
          if (c.status === 'expiring') return `EXPIRING ${c.expiration_date || ''}`;
          return c.expiration_date || 'CURRENT';
        }),
      ];
      lines.push(cells.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `credential_audit_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const visibleRows = useMemo(() => {
    if (!report) return [];
    let rows = report.rows;
    if (showOnlyIssues) {
      rows = rows.filter(r => (r.summary.missing + r.summary.expired + r.summary.expiring) > 0);
    }
    return rows;
  }, [report, showOnlyIssues]);

  // A credential row is visible when it matches the active status filter AND,
  // when "show only issues" is on, only issue statuses (missing/expired/expiring)
  // are shown — current/ok credentials are hidden per therapist.
  const isCredentialVisible = (c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (showOnlyIssues && !['missing', 'expired', 'expiring'].includes(c.status)) return false;
    return true;
  };

  // All document IDs that are currently visible AND selectable (have an actual file)
  const visibleSelectableDocIds = useMemo(() => {
    const ids = [];
    visibleRows.forEach(row => {
      row.credentials.forEach(c => {
        if (!isCredentialVisible(c)) return;
        ids.push(credKey(row, c));
      });
    });
    return ids;
  }, [visibleRows, statusFilter, showOnlyIssues]);

  const toggleDoc = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const togglePersonRow = (row) => {
    const personDocIds = row.credentials
      .filter(isCredentialVisible)
      .map(c => credKey(row, c));
    if (personDocIds.length === 0) return;
    const allSelected = personDocIds.every(id => selectedDocIds.has(id));
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      personDocIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const toggleAllVisible = () => {
    if (visibleSelectableDocIds.length === 0) return;
    const allSelected = visibleSelectableDocIds.every(id => selectedDocIds.has(id));
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      visibleSelectableDocIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  // Open a pre-filled reminder Compose modal for a single credential row.
  const openCredentialReminder = (row, c) => {
    const recipientEmail = row.email || row.work_email;
    if (!recipientEmail) {
      toast.error(`${row.name} has no email on file — add one on their profile first`);
      return;
    }
    const prefill = buildCredentialReminder({
      recipientName: row.name,
      recipientEmail,
      credentials: [{
        type: c.type,
        status: c.status,
        expiration_date: c.expiration_date,
      }],
    });
    setReminderPrefill(prefill);
  };

  // Bulk: open a setup step (pick sender mailbox + resolve missing emails)
  // before firing the per-person reminder queue.
  const openBulkSetup = async () => {
    if (!report || selectedDocIds.size === 0) {
      toast.error('Select at least one credential first');
      return;
    }
    const byPerson = new Map();
    report.rows.forEach(row => {
      row.credentials.forEach(c => {
        if (selectedDocIds.has(credKey(row, c))) {
          if (!byPerson.has(row.personnel_id)) byPerson.set(row.personnel_id, { row, creds: [] });
          byPerson.get(row.personnel_id).creds.push(c);
        }
      });
    });
    if (byPerson.size === 0) {
      toast.error('Select at least one credential first');
      return;
    }
    const people = Array.from(byPerson.values()).map(({ row, creds }) => ({
      personnel_id: row.personnel_id,
      name: row.name,
      row,
      creds,
      email: (row.email || row.work_email || '').trim(),
      inputEmail: '',
      skip: false,
    }));
    setBulkSetup({ people });
    // Load mailbox connections so the sender picker can show emails / disable unavailable ones.
    const [s, p] = await Promise.all([
      axios.get(`${API}/communications/connection-status`).catch(() => null),
      axios.get(`${API}/communications/personal/connection-status`).catch(() => null),
    ]);
    const shared = s?.data || null;
    const personal = p?.data || null;
    setSenderConns({ shared, personal });
    setBulkMailbox(personal?.connected && !shared?.connected ? 'personal' : 'shared');
  };

  const updateBulkPerson = (idx, patch) => {
    setBulkSetup(prev => {
      if (!prev) return prev;
      const people = prev.people.map((p, i) => (i === idx ? { ...p, ...patch } : p));
      return { ...prev, people };
    });
  };

  const startBulkFromSetup = () => {
    if (!bulkSetup) return;
    const queue = [];
    const unresolved = [];
    bulkSetup.people.forEach(p => {
      if (p.skip) return;
      const email = (p.email || p.inputEmail || '').trim();
      if (!email) { unresolved.push(p.name); return; }
      queue.push({ row: p.row, creds: p.creds, email });
    });
    if (unresolved.length) {
      toast.error(`Enter an email or tick Skip for: ${unresolved.slice(0, 4).join(', ')}${unresolved.length > 4 ? '…' : ''}`);
      return;
    }
    if (queue.length === 0) {
      toast.error('Everyone is skipped — nothing to send');
      return;
    }
    setBulkSetup(null);
    setBulkRemindQueue(queue);
    const first = queue[0];
    setReminderPrefill(buildCredentialReminder({
      recipientName: first.row.name,
      recipientEmail: first.email,
      credentials: first.creds,
    }));
  };

  // Bulk: send a single email per person summarizing all selected credentials
  // for that person. Opens Compose modal pre-filled for one person at a time.
  const [bulkRemindQueue, setBulkRemindQueue] = useState([]);  // pending [{name, email, creds}]

  const advanceBulkQueue = () => {
    setBulkRemindQueue(prev => {
      const next = prev.slice(1);
      if (next.length > 0) {
        const head = next[0];
        // Defer opening until React closes current modal to avoid prefill race
        setTimeout(() => {
          setReminderPrefill(buildCredentialReminder({
            recipientName: head.row.name,
            recipientEmail: head.email,
            credentials: head.creds,
          }));
        }, 150);
      }
      return next;
    });
  };

  const [missingFilesModal, setMissingFilesModal] = useState(null); // {count, docs:[{document_id, document_name, personnel_name}]}

  const downloadSelected = async () => {
    if (realDocIds.length === 0) {
      toast.error('Select at least one document to download');
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/admin/credentials/bulk-download`,
        { document_ids: realDocIds },
        { responseType: 'blob' }
      );
      const included = parseInt(res.headers['x-documents-included'] || '0', 10);
      const skipped = parseInt(res.headers['x-documents-skipped'] || '0', 10);

      // Safety net: if the server somehow returned a 200 with 0 included, don't
      // trigger a confusing "empty archive" save.
      if (included === 0) {
        toast.error('No files were available to download — see Files Missing details below');
        return;
      }

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credential_audit_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      if (skipped > 0) {
        toast.warning(`Downloaded ${included} document(s) — ${skipped} were skipped because their files are missing from server storage`, { duration: 8000 });
      } else {
        toast.success(`Downloaded ${included} document(s)`);
      }
    } catch (e) {
      // The 404 "all files missing" path returns a structured JSON detail in a blob.
      let detail = 'Failed to download documents';
      let structured = null;
      try {
        const text = await e.response?.data?.text?.();
        if (text) {
          const parsed = JSON.parse(text);
          if (typeof parsed.detail === 'string') {
            detail = parsed.detail;
          } else if (parsed.detail && typeof parsed.detail === 'object') {
            structured = parsed.detail;
            detail = parsed.detail.message || detail;
          }
        }
      } catch (_) { /* ignore */ }

      if (structured) {
        setMissingFilesModal({
          count: structured.skipped_count || 0,
          docs: structured.skipped_documents || [],
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft size={14} className="mr-1" /> Back to Admin</Button></Link>

      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ClipboardCheck className="text-primary" /> Credential Audit
        </h1>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          Pick any number of therapists and see which credentials they're missing, which are expired,
          and which are still current — with expiration dates. Great for compliance audits or onboarding spot-checks.
        </p>
      </div>

      {/* Selection panel */}
      <Card className="p-5 space-y-4">
        {/* Filter by Agency — auto-populates therapist list */}
        <div className="border rounded-md p-3 bg-blue-50/40">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <Label className="flex items-center gap-1.5 text-sm font-semibold">
              <Building2 size={14} /> Filter by Agency ({selectedAgencyIds.size} selected)
            </Label>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={agencySinceDays}
                onChange={(e) => setAgencySinceDays(parseInt(e.target.value))}
                className="text-xs border rounded px-2 py-1 bg-white"
                data-testid="agency-timeframe"
              >
                <option value="0">All time</option>
                <option value="30">Last 30 days</option>
                <option value="60">Last 60 days</option>
                <option value="90">Last 90 days</option>
                <option value="180">Last 6 months</option>
                <option value="365">Last 12 months</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => { setSelectedAgencyIds(new Set()); setAgencySearch(''); }}>
                Clear agencies
              </Button>
              <Button size="sm" onClick={applyAgencyFilter} disabled={loadingAgencyFilter || selectedAgencyIds.size === 0} data-testid="apply-agency-filter-btn">
                {loadingAgencyFilter ? 'Finding…' : 'Apply → Pick Therapists'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Pick agencies and click "Apply" to auto-select every therapist who has logged visits there. You can then refine the selection below before running the audit.
          </p>
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={agencySearch}
              onChange={(e) => setAgencySearch(e.target.value)}
              placeholder="Filter agencies by name…"
              className="pl-7 h-9 text-sm bg-white"
              data-testid="agency-search-input"
            />
          </div>
          <div className="border rounded-md max-h-[180px] overflow-y-auto bg-white">
            {filteredAgencies.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-3">No agencies match</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {filteredAgencies.map(a => (
                  <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30 cursor-pointer text-sm border-b last:border-b-0">
                    <input
                      type="checkbox"
                      checked={selectedAgencyIds.has(a.id)}
                      onChange={() => toggleAgency(a.id)}
                      data-testid={`agency-${a.id}`}
                    />
                    <span className="font-medium truncate">{a.agency_name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* People */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Therapists ({selectedIds.size} selected)</Label>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set(filteredPeople.map(p => p.id)))} data-testid="select-all-people">
                  Select all
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="clear-people">
                  Clear
                </Button>
              </div>
            </div>
            <div className="relative mb-2">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name…"
                className="pl-7 h-9 text-sm"
                data-testid="audit-people-search"
              />
            </div>
            <div className="border rounded-md max-h-[280px] overflow-y-auto">
              {filteredPeople.map(p => (
                <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30 cursor-pointer text-sm border-b last:border-b-0">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggleId(p.id)}
                    data-testid={`person-${p.id}`}
                  />
                  <span className="font-medium">{p.first_name} {p.last_name}</span>
                  {p.discipline && <span className="text-xs text-muted-foreground">{p.discipline}</span>}
                </label>
              ))}
              {filteredPeople.length === 0 && <p className="text-center text-sm text-muted-foreground py-3">No matches</p>}
            </div>
          </div>

          {/* Types */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Credential types</Label>
              <Button size="sm" variant="ghost" onClick={() => setSelectedTypes(new Set())}>Reset (all)</Button>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Leave empty to check ALL active credential types. Pick specific ones to narrow the audit.
            </p>
            <div className="border rounded-md max-h-[280px] overflow-y-auto p-2">
              <div className="flex flex-wrap gap-1.5">
                {allTypes.map(t => {
                  const active = selectedTypes.has(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        const next = new Set(selectedTypes);
                        if (next.has(t)) next.delete(t); else next.add(t);
                        setSelectedTypes(next);
                      }}
                      className={`text-xs px-2 py-1 rounded border transition-colors ${
                        active ? 'bg-primary text-white border-primary' : 'bg-background hover:bg-muted'
                      }`}
                      data-testid={`type-chip-${t}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Settings + Run */}
          <div className="space-y-3">
            <div>
              <Label>"Expiring soon" window (days)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={warningDays}
                onChange={(e) => setWarningDays(e.target.value)}
                className="h-9"
                data-testid="warning-days-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Documents expiring within this window are flagged amber. Already-expired ones are red.
              </p>
            </div>
            <div className="pt-2">
              <Button onClick={runReport} disabled={loading || selectedIds.size === 0} className="w-full" data-testid="run-audit-btn">
                {loading ? <><Loader2 className="animate-spin mr-2" size={14} /> Running…</> : <><ShieldCheck size={14} className="mr-1" /> Run Audit</>}
              </Button>
            </div>
            {report && (
              <Button onClick={downloadCsv} variant="outline" className="w-full" data-testid="download-csv-btn">
                <Download size={14} className="mr-1" /> Download CSV
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Results */}
      {report && (
        <Card className="p-5 space-y-4" data-testid="audit-results">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold">Audit Results</h3>
              <span className="text-xs text-muted-foreground">{report.rows.length} therapist(s) · {report.credential_types.length} type(s)</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Bulk download toolbar */}
              {visibleSelectableDocIds.length > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer" title="Select / deselect every visible document">
                  <input
                    type="checkbox"
                    checked={visibleSelectableDocIds.length > 0 && visibleSelectableDocIds.every(id => selectedDocIds.has(id))}
                    onChange={toggleAllVisible}
                    data-testid="audit-select-all-docs"
                  />
                  Select all visible ({visibleSelectableDocIds.length})
                </label>
              )}
              <Button
                size="sm"
                onClick={downloadSelected}
                disabled={realDocIds.length === 0 || downloading}
                data-testid="audit-bulk-download-btn"
                className="gap-2"
              >
                <Download size={14} />
                {downloading ? 'Bundling…' : `Download ${realDocIds.length > 0 ? realDocIds.length : ''} as ZIP`.trim()}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (realDocIds.length === 0) { toast.error('Select at least one uploaded credential first'); return; }
                  setEmailAttachPrefill({
                    document_ids: realDocIds,
                    label: `${realDocIds.length} selected credential${realDocIds.length !== 1 ? 's' : ''}`,
                  });
                }}
                disabled={realDocIds.length === 0}
                data-testid="audit-bulk-email-attach-btn"
                className="gap-2"
              >
                <Paperclip size={14} />
                Email selected ({realDocIds.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={openBulkSetup}
                disabled={selectedDocIds.size === 0}
                data-testid="audit-bulk-remind-btn"
                className="gap-2"
              >
                <Mail size={14} />
                Remind ({selectedDocIds.size || 0})
              </Button>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={showOnlyIssues} onChange={(e) => setShowOnlyIssues(e.target.checked)} data-testid="show-only-issues" />
                Show only therapists with issues
              </label>
            </div>
          </div>

          {/* Grand totals */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { key: 'current',  Icon: ShieldCheck,    label: 'Current',  color: 'emerald' },
              { key: 'expiring', Icon: AlertTriangle,  label: 'Expiring', color: 'amber'   },
              { key: 'expired',  Icon: AlertCircle,    label: 'Expired',  color: 'red'     },
              { key: 'missing',  Icon: X,              label: 'Missing',  color: 'gray'    },
            ].map(({ key, Icon, label, color }) => (
              <div key={key} className={`rounded-md border p-3 bg-${color}-50 border-${color}-200`}>
                <div className={`flex items-center gap-1.5 text-${color}-700 text-xs font-semibold uppercase tracking-wide`}>
                  <Icon size={12} /> {label}
                </div>
                <div className={`text-2xl font-bold mt-1 text-${color}-700`}>{report.summary[key]}</div>
              </div>
            ))}
          </div>

          {/* Per-therapist breakdown */}
          {visibleRows.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {showOnlyIssues
                ? <><ShieldCheck className="mx-auto text-emerald-500 mb-2" size={32} /> Everyone selected is fully compliant! 🎉</>
                : 'No results.'}
            </div>
          ) : (
            <div className="space-y-3">
              {visibleRows.map(row => {
                const personDocIds = row.credentials
                  .filter(isCredentialVisible)
                  .map(c => credKey(row, c));
                const personAllSelected = personDocIds.length > 0 && personDocIds.every(id => selectedDocIds.has(id));
                const personSomeSelected = personDocIds.some(id => selectedDocIds.has(id));
                return (
                <div key={row.personnel_id} className="border rounded-md overflow-hidden" data-testid={`audit-row-${row.personnel_id}`}>
                  <div className="bg-muted/40 px-4 py-2 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      {personDocIds.length > 0 && (
                        <input
                          type="checkbox"
                          checked={personAllSelected}
                          ref={el => { if (el) el.indeterminate = !personAllSelected && personSomeSelected; }}
                          onChange={() => togglePersonRow(row)}
                          data-testid={`audit-person-checkbox-${row.personnel_id}`}
                          title={`Select all ${personDocIds.length} document(s) for ${row.name}`}
                        />
                      )}
                      <Link to={`/personnel/${row.personnel_id}`} className="font-semibold hover:underline" target="_blank">
                        {row.name}
                      </Link>
                      {row.discipline && <span className="text-xs text-muted-foreground">{row.discipline}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {row.summary.expired > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700">{row.summary.expired} expired</span>}
                      {row.summary.expiring > 0 && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">{row.summary.expiring} expiring</span>}
                      {row.summary.missing > 0 && <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">{row.summary.missing} missing</span>}
                      {row.summary.current > 0 && <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{row.summary.current} current</span>}
                    </div>
                  </div>
                  <div className="divide-y">
                    {row.credentials
                      .filter(isCredentialVisible)
                      .map((c, i) => (
                        <div key={i} className="px-4 py-2 flex items-center justify-between text-sm hover:bg-muted/20">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedDocIds.has(credKey(row, c))}
                              onChange={() => toggleDoc(credKey(row, c))}
                              title={c.document_id ? undefined : 'Missing — select to include in a reminder'}
                              data-testid={c.document_id ? `audit-doc-checkbox-${c.document_id}` : `audit-missing-checkbox-${row.personnel_id}-${c.type}`}
                            />
                            <span className={`w-2 h-2 rounded-full ${STATUS_DOT[c.status]}`} />
                            <span className="font-medium">{c.type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_BADGE[c.status]}`}>
                              {c.status === 'missing' ? 'MISSING' : c.expiration_date || c.status.toUpperCase()}
                            </span>
                            {c.requested && (
                              <span
                                className="text-xs px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 flex items-center gap-1"
                                title={`Signature form sent${c.requested_at ? ' ' + new Date(c.requested_at).toLocaleDateString() : ''}${c.requested_template ? ' · ' + c.requested_template : ''}`}
                                data-testid={`audit-requested-${row.personnel_id}-${c.type}`}
                              >
                                <Send size={11} /> Requested
                              </span>
                            )}
                            {c.status !== 'missing' && c.document_id && (
                              <button
                                type="button"
                                onClick={() => setPreviewing({
                                  document_id: c.document_id,
                                  document_name: c.type,
                                  file_name: c.file_name,
                                })}
                                className="text-muted-foreground hover:text-foreground"
                                title="Preview"
                                data-testid={`audit-preview-${c.document_id}`}
                              >
                                <Eye size={13} />
                              </button>
                            )}
                            {c.document_id && (
                              <button
                                type="button"
                                onClick={() => setEditingDocId(c.document_id)}
                                className="text-muted-foreground hover:text-foreground"
                                title="Edit credential info"
                                data-testid={`audit-edit-${c.document_id}`}
                              >
                                <Pencil size={13} />
                              </button>
                            )}
                            {c.status !== 'current' && (
                              <button
                                type="button"
                                onClick={() => openCredentialReminder(row, c)}
                                className="text-amber-600 hover:text-amber-800"
                                title={`Remind ${row.name} about this credential`}
                                data-testid={`audit-remind-${row.personnel_id}-${c.type}`}
                              >
                                <Bell size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {missingFilesModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setMissingFilesModal(null)}
          data-testid="missing-files-modal"
        >
          <div
            className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b">
              <h3 className="font-heading text-lg font-semibold text-foreground">
                Files missing from server storage
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                None of the {missingFilesModal.count} selected document(s) have files available on the server. These records exist in the database but their underlying files were lost (typically by a previous redeploy before durable storage was enabled). Re-upload the original files via each person's profile.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <ul className="space-y-2">
                {missingFilesModal.docs.map((d) => (
                  <li
                    key={d.document_id}
                    className="flex items-center justify-between gap-3 p-2 rounded border bg-amber-50/40"
                    data-testid={`missing-file-${d.document_id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{d.document_name || 'Untitled'}</div>
                      <div className="text-xs text-muted-foreground truncate">{d.personnel_name}</div>
                    </div>
                    {d.personnel_id && (
                      <Link
                        to={`/personnel/${d.personnel_id}`}
                        target="_blank"
                        className="text-xs text-primary hover:underline shrink-0"
                      >
                        Open profile →
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 border-t flex justify-end">
              <Button onClick={() => setMissingFilesModal(null)} data-testid="missing-files-modal-close">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <DocumentPreviewModal
        open={previewing !== null}
        onClose={() => setPreviewing(null)}
        previewUrl={previewing ? `${API}/documents/${previewing.document_id}/preview` : null}
        downloadUrl={previewing ? `${API}/documents/${previewing.document_id}/download` : null}
        fileName={previewing?.file_name}
        title={previewing?.document_name}
      />

      <EditCredentialModal
        open={editingDocId !== null}
        documentId={editingDocId}
        onClose={() => setEditingDocId(null)}
        onSaved={() => { setEditingDocId(null); runReport(); }}
      />

      {bulkSetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bulk-remind-setup-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold flex items-center gap-2"><Mail size={16} /> Send reminders</h3>
              <button onClick={() => setBulkSetup(null)} className="text-muted-foreground hover:text-foreground" data-testid="bulk-remind-close"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-4 overflow-y-auto">
              {/* Sender picker */}
              <div>
                <label className="text-sm font-medium block mb-1.5">Send from</label>
                <div className="flex flex-col gap-2">
                  <label className={`flex items-center gap-2 text-sm rounded border px-3 py-2 cursor-pointer ${bulkMailbox === 'shared' ? 'border-primary bg-primary/5' : 'border-muted'} ${!senderConns.shared?.connected ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input type="radio" name="bulk-sender" value="shared" checked={bulkMailbox === 'shared'} disabled={!senderConns.shared?.connected} onChange={() => setBulkMailbox('shared')} data-testid="bulk-sender-admin" />
                    <span>Admin (shared mailbox){senderConns.shared?.email ? ` — ${senderConns.shared.email}` : senderConns.shared?.connected ? '' : ' — not connected'}</span>
                  </label>
                  <label className={`flex items-center gap-2 text-sm rounded border px-3 py-2 cursor-pointer ${bulkMailbox === 'personal' ? 'border-primary bg-primary/5' : 'border-muted'} ${!senderConns.personal?.connected ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input type="radio" name="bulk-sender" value="personal" checked={bulkMailbox === 'personal'} disabled={!senderConns.personal?.connected} onChange={() => setBulkMailbox('personal')} data-testid="bulk-sender-me" />
                    <span>Me (my Gmail){senderConns.personal?.email ? ` — ${senderConns.personal.email}` : senderConns.personal?.connected ? '' : ' — connect your Gmail in Communications first'}</span>
                  </label>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="text-sm font-medium block mb-1.5">Recipients ({bulkSetup.people.filter(p => !p.skip && (p.email || p.inputEmail.trim())).length} of {bulkSetup.people.length})</label>
                <div className="border rounded divide-y max-h-[40vh] overflow-y-auto">
                  {bulkSetup.people.map((p, idx) => (
                    <div key={p.personnel_id} className={`flex items-center gap-3 px-3 py-2 text-sm ${p.skip ? 'opacity-50' : ''}`} data-testid={`bulk-person-${p.personnel_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.name} <span className="text-xs text-muted-foreground">· {p.creds.length} credential{p.creds.length !== 1 ? 's' : ''}</span></div>
                        {p.email ? (
                          <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                        ) : (
                          <input
                            type="email"
                            placeholder="No email on file — type one to include"
                            value={p.inputEmail}
                            onChange={(e) => updateBulkPerson(idx, { inputEmail: e.target.value })}
                            disabled={p.skip}
                            className="mt-1 w-full text-xs border rounded px-2 py-1"
                            data-testid={`bulk-email-input-${p.personnel_id}`}
                          />
                        )}
                      </div>
                      {!p.email && (
                        <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                          <input type="checkbox" checked={p.skip} onChange={(e) => updateBulkPerson(idx, { skip: e.target.checked })} data-testid={`bulk-skip-${p.personnel_id}`} />
                          Skip
                        </label>
                      )}
                    </div>
                  ))}
                </div>
                {bulkSetup.people.some(p => !p.email) && (
                  <p className="text-xs text-amber-600 mt-1.5">Some people have no email on file — type one to include them, or tick Skip.</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t">
              <Button variant="outline" size="sm" onClick={() => setBulkSetup(null)} data-testid="bulk-remind-cancel">Cancel</Button>
              <Button size="sm" onClick={startBulkFromSetup} data-testid="bulk-remind-start" className="gap-2">
                <Send size={14} /> Review &amp; send
              </Button>
            </div>
          </div>
        </div>
      )}

      <ComposeModal
        open={reminderPrefill !== null}
        onClose={() => {
          setReminderPrefill(null);
          // If there's a bulk queue in flight, drop the head so user can stop mid-batch
          setBulkRemindQueue(q => q.slice(1));
        }}
        onSent={() => {
          setReminderPrefill(null);
          if (bulkRemindQueue.length > 1) {
            toast.success(`Reminder sent. ${bulkRemindQueue.length - 1} more queued.`);
            advanceBulkQueue();
          } else {
            setBulkRemindQueue([]);
          }
        }}
        mode="new"
        mailbox={bulkRemindQueue.length > 0 ? bulkMailbox : null}
        initialTo={reminderPrefill?.to || ''}
        initialSubject={reminderPrefill?.subject || ''}
        initialBody={reminderPrefill?.body || ''}
      />

      <ComposeModal
        open={emailAttachPrefill !== null}
        onClose={() => setEmailAttachPrefill(null)}
        onSent={() => setEmailAttachPrefill(null)}
        mode="new"
        attachDocumentIds={emailAttachPrefill?.document_ids || null}
        attachLabel={emailAttachPrefill?.label || ''}
      />
    </div>
  );
}
